import React, {useState} from 'react';
import {
  Text,
  FormField,
  FormFieldHelperText,
  FormFieldLabel,
  H3,
  Input,
  FlexItem,
  StackLayout, RadioButtonGroup, RadioButton
} from '@salt-ds/core';
import PageNavigation from '../../components/common/page-navigation/PageNavigation';
import Constants from '../../components/common/Constants';
import {useNavigate} from 'react-router';
import {formatBillingZipCode, formatCardNumber, formatPhoneNumber, isCanadianZipCode} from '../../common/ApiUtils';
import {credentialRecoverySubmit} from "../../api/ForgotUserOrOrgIdCardApi";

export default function ForgotUserOrOrgIdCardForm(props) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    commercialCardNumber: "",
    cardBillingZipCode: "",
    cardBusinessPhoneNumber: ""
  });
  const [errors, setErrors] = useState({
    commercialCardNumber: false,
    verificationOption: {required: false, invalid: false}
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const[selectedAuthenticationOption, setSelectedAuthenticationOption] = useState("");

  const handleChange = (event) => {
    let {name, value} = event.target;

    if (name === "commercialCardNumber") {
      let formattedValue = value.replace(/\D/g, '');
      value = formatCardNumber(formattedValue);
    } else if (name === "cardBillingZipCode") {
      value = formatBillingZipCode(value);
    } else if (name === "cardBusinessPhoneNumber") {
      let formattedValue = value.replace(/\D/g, '');
      value = formatPhoneNumber(formattedValue);
    }
    setFormData((prevFormData) => ({...prevFormData, [name]: value}));

    //Clear error when user begins to type
    if (name === 'commercialCardNumber') {
      setErrors((prevErrors) => ({
        ...prevErrors,
        commercialCardNumber: false,
        commercialCardNumberInvalid: false,
      }));
    } else if (name === 'cardBillingZipCode' || name === 'cardBusinessPhoneNumber') {
      setErrors((prevErrors) => ({
        ...prevErrors,
        verificationOption: {required: false, invalid: false}
      }));
      if (props.setHasError) props.setHasError(false); // Remove error banner when input changes
    }
  };

  //Validation for empty fields & format
  // Helper functions to reduce cognitive complexity
  const validateCommercialCardNumber = (formData, cardDigits) => ({
    commercialCardNumber: (formData.commercialCardNumber || '').trim() === '',
    commercialCardNumberInvalid: cardDigits.length > 0 && cardDigits.length < 16,
  });

  const validateVerificationOptionRequired = (isZipSelected, isPhoneSelected, formData) => {
    if (!isZipSelected && !isPhoneSelected) return true;
    if (isZipSelected && (formData.cardBillingZipCode || '').trim() === '') return true;
    if (isPhoneSelected && (formData.cardBusinessPhoneNumber || '').trim() === '') return true;
    return false;
  };

  const validateZipCodeFormat = (zipCode) => {
    const zip = zipCode || '';
    if (isCanadianZipCode(zip)) {
      return zip.length !== 7;
    }
    return zip.length < 5 || (zip.length > 5 && zip.length < 9);
  };

  const validateVerificationOptionInvalid = (isZipSelected, isPhoneSelected, formData, phoneDigits) => {
    if (isZipSelected && validateZipCodeFormat(formData.cardBillingZipCode)) {
      return true;
    }
    if (isPhoneSelected && formData.cardBusinessPhoneNumber.trim() !== '' && phoneDigits.length < 10) {
      return true;
    }
    return false;
  };
  // Refactored validateFormData function
  const validateFormData = (formData) => {
    const phoneDigits = formData.cardBusinessPhoneNumber.replace(/\D/g, '');
    const cardDigits = formData.commercialCardNumber.replace(/\D/g, '');
    const isZipSelected = selectedAuthenticationOption === "cardBillingZipCode";
    const isPhoneSelected = selectedAuthenticationOption === "cardBusinessPhoneNumber";

    const cardValidation = validateCommercialCardNumber(formData, cardDigits);

    return {
      ...cardValidation,
      verificationOption: {
        required: validateVerificationOptionRequired(isZipSelected, isPhoneSelected, formData),
        invalid: validateVerificationOptionInvalid(isZipSelected, isPhoneSelected, formData, phoneDigits),
      },
    };
  };

  const handleResponse = (response) => {
    if (response.data) {
      sessionStorage.setItem('userEmail', response.data.email);
      navigate(Constants.forgotUserOrOrgIdComplete, { state: { formData, from: Constants.forgotUserOrOrgIdCard } });
    } else if (response.error && response.error.detail) {
      resetFormData();
      props.showErrorOnPage(response.error.detail);
    }
  };

  const resetFormData = () => {
    setFormData({ commercialCardNumber: "", cardBillingZipCode: "", cardBusinessPhoneNumber: "" });
  };

  const handleSubmit = async (event) => {
    props.setIsLoading(true);
    setIsSubmitting(true);
    event.preventDefault();

    const newErrors = validateFormData(formData);
    setErrors(newErrors);

    // Show error banner only if commercial card number is filled and no radio button is selected
    if (formData.commercialCardNumber.trim() !== '' && !selectedAuthenticationOption) {
      if (props.setHasError) props.setHasError(true);
      if (props.showErrorOnPage) props.showErrorOnPage("Please select an authentication option to continue.");
    } else {
      if (props.setHasError) props.setHasError(false);
    }

    if (!newErrors.commercialCardNumber && !newErrors.commercialCardNumberInvalid && !newErrors.verificationOption.required && !newErrors.verificationOption.invalid) {
      try {
        const response = await credentialRecoverySubmit(formData.commercialCardNumber, formData.cardBillingZipCode, formData.cardBusinessPhoneNumber);
        handleResponse(response);
        setIsSubmitting(false);
      } catch (error) {
        setIsSubmitting(false);
        throw error;
      } finally {
        props.setIsLoading(false);
      }
    }
    else {
      props.setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const hasVerificationOptionError = () => {
    return errors.verificationOption.required || errors.verificationOption.invalid;
  };

  return (
      <form autoComplete="off" data-testid="forgot-user-org-id-card-form" onSubmit={handleSubmit}>
        <fieldset disabled={isSubmitting} style={{border:0, padding:0, margin:0}}>
          <StackLayout gap={2}>
            <FormField data-testid="forgot-user-org-id-form" validationStatus={errors.commercialCardNumber || errors.commercialCardNumberInvalid ? "error" : undefined} className="form-layout">
              <FormFieldLabel htmlFor="commercialCardNumber">{Constants.commercialCardNumberLabel}</FormFieldLabel>
              <Input id="commercialCardNumber" inputProps={{
                name: "commercialCardNumber",
                maxLength: "19", value: formData.commercialCardNumber,
                "aria-required": "true",
                "aria-describedby": errors.commercialCardNumber ? "commercialCardNumber-error" : undefined,
              }} onChange={handleChange} data-testid="commercial-card-number"
                     required />
              {errors.commercialCardNumber && (
                <FormFieldHelperText id="commercialCardNumber-error" role="alert">
                  {Constants.requiredFieldError}
                </FormFieldHelperText>
              )}
              {errors.commercialCardNumberInvalid && (
                <FormFieldHelperText id="commercialCardNumber-error" role="alert">
                  {Constants.commercialCardNumberHelper}
                </FormFieldHelperText>
              )}
              {!errors.commercialCardNumber && (
                <FormFieldHelperText>
                  {Constants.commercialCardNumberHelper}
                </FormFieldHelperText>
              )}
            </FormField>
            <FlexItem>
              <H3>
                {Constants.forgotUserOrOrgIdAdditionalOptionInstructionalText}
              </H3>
              <Text>
                Please choose one.
              </Text>
            </FlexItem>
            <RadioButtonGroup value={selectedAuthenticationOption} direction="vertical"
                              onChange={(e) => {
                                setSelectedAuthenticationOption(e.target.value);
                                if(props.setHasError) props.setHasError(false); // Remove error banner when radio button changes
                              }}>

              <RadioButton data-testid="billing-zip-code-radio-button" label={Constants.cardBillingZipCodeLabel} value="cardBillingZipCode"
                onKeyDown={e => {
                  if (e.key === " " || e.key === "Enter") {
                    setSelectedAuthenticationOption("cardBillingZipCode");
                    e.preventDefault();
                  }
                }}/>
              {selectedAuthenticationOption === "cardBillingZipCode" && (
                  <FormField validationStatus={hasVerificationOptionError() ? "error" : undefined} className="form-layout">
                    <FormFieldLabel className="radio-label" htmlFor="cardBillingZipCode">{Constants.cardBillingZipCodeLabel}</FormFieldLabel>
                    <Input id="cardBillingZipCode" inputProps={{
                      name: "cardBillingZipCode",
                      maxLength: "9",
                      value: formData.cardBillingZipCode,
                      "aria-describedby": (hasVerificationOptionError()) ? "cardBillingZipCode-error" : "cardBillingZipCode-helper"
                    }}
                           onChange={handleChange}
                           required />
                    {(hasVerificationOptionError()) ? (
                      <FormFieldHelperText id="cardBillingZipCode-error" role="alert">
                        {errors.verificationOption.required ? Constants.requiredFieldError : Constants.cardBillingZipCodeHelper}
                      </FormFieldHelperText>
                    ) : (
                      <FormFieldHelperText id="cardBillingZipCode-helper">
                        {Constants.cardBillingZipCodeHelper}
                      </FormFieldHelperText>
                    )}                  </FormField>
              )}

              <RadioButton data-testid="business-phone-number-radio-button" label={Constants.cardBusinessPhoneNumberLabel} value="cardBusinessPhoneNumber"
                onKeyDown={e => {
                  if (e.key === " " || e.key === "Enter") {
                    setSelectedAuthenticationOption("cardBusinessPhoneNumber");
                    e.preventDefault();
                  }
                }}/>
              {selectedAuthenticationOption === "cardBusinessPhoneNumber" && (
                  <FormField validationStatus={hasVerificationOptionError() ? "error" : undefined} className="form-layout">
                    <FormFieldLabel className="radio-label" data-testid="business-phone-number-input" htmlFor="cardBusinessPhoneNumber">{Constants.cardBusinessPhoneNumberLabel}</FormFieldLabel>
                    <Input id="cardBusinessPhoneNumber"
                           inputProps={{
                             name: "cardBusinessPhoneNumber",
                             maxLength: "12",
                             value: formData.cardBusinessPhoneNumber,
                             "aria-describedby": (hasVerificationOptionError()) ? "cardBusinessPhoneNumber-error" : "cardBusinessPhoneNumber-helper"
                           }}
                           onChange={handleChange}
                           required />
                    {(hasVerificationOptionError()) ? (
                      <FormFieldHelperText id="cardBusinessPhoneNumber-error" role="alert">
                        {errors.verificationOption.required ? Constants.requiredFieldError : Constants.cardBusinessPhoneNumberHelper}
                      </FormFieldHelperText>
                    ) : (
                      <FormFieldHelperText id="cardBusinessPhoneNumber-helper">
                        {Constants.cardBusinessPhoneNumberHelper}
                      </FormFieldHelperText>
                    )}

                  </FormField>
              )}
            </RadioButtonGroup>
          </StackLayout>
          <PageNavigation style={{marginTop: "20%"}} />
        </fieldset>
      </form>
  );
}